import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Clock, User, Calendar, RefreshCw, Settings, Wifi, WifiOff } from 'lucide-react';
import { getSocket } from '../services/socket';
import api from '../services/api';

/**
 * Pantalla de incidencias en tiempo real
 * Disenada para mostrar en multiples monitores/pantallas
 * Muestra: estado, fecha de apertura, quien la gestiona, gravedad
 */
export default function IncidenciasDisplay() {
  const { pin } = useParams(); // PIN desde /display/:pin
  const [searchParams] = useSearchParams(); // screen desde /pantallas?screen=123
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [connected, setConnected] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [screenConfig, setScreenConfig] = useState(null);
  const [justUpdated, setJustUpdated] = useState(false);
  const [settings, setSettings] = useState({
    autoRefresh: true,
    refreshInterval: 30,
    showResolved: false,
    fontSize: 'normal',
    theme: 'dark',
    skin: 'default',
    accentColor: 'blue'
  });

  // Definición de skins y colores
  const SKINS = {
    default: { name: 'Estándar', dark: 'bg-gray-900', light: 'bg-gray-100', card: 'bg-gray-800', cardLight: 'bg-white' },
    corporate: { name: 'Corporativo', dark: 'bg-slate-900', light: 'bg-slate-50', card: 'bg-slate-800', cardLight: 'bg-slate-100' },
    midnight: { name: 'Medianoche', dark: 'bg-indigo-950', light: 'bg-indigo-50', card: 'bg-indigo-900', cardLight: 'bg-indigo-100' },
    forest: { name: 'Bosque', dark: 'bg-emerald-950', light: 'bg-emerald-50', card: 'bg-emerald-900', cardLight: 'bg-emerald-100' },
    sunset: { name: 'Atardecer', dark: 'bg-orange-950', light: 'bg-orange-50', card: 'bg-orange-900', cardLight: 'bg-orange-100' },
    ocean: { name: 'Océano', dark: 'bg-cyan-950', light: 'bg-cyan-50', card: 'bg-cyan-900', cardLight: 'bg-cyan-100' },
    volcano: { name: 'Volcán', dark: 'bg-red-950', light: 'bg-red-50', card: 'bg-red-900', cardLight: 'bg-red-100' },
    purple: { name: 'Púrpura', dark: 'bg-purple-950', light: 'bg-purple-50', card: 'bg-purple-900', cardLight: 'bg-purple-100' },
    matrix: { name: 'Matrix', dark: 'bg-black', light: 'bg-green-50', card: 'bg-green-950', cardLight: 'bg-green-100' },
    neon: { name: 'Neón', dark: 'bg-zinc-950', light: 'bg-zinc-50', card: 'bg-zinc-900', cardLight: 'bg-zinc-100' }
  };

  const ACCENT_COLORS = {
    blue: { name: 'Azul', primary: 'blue-500', hover: 'blue-600', light: 'blue-100', text: 'blue-700' },
    green: { name: 'Verde', primary: 'green-500', hover: 'green-600', light: 'green-100', text: 'green-700' },
    red: { name: 'Rojo', primary: 'red-500', hover: 'red-600', light: 'red-100', text: 'red-700' },
    purple: { name: 'Morado', primary: 'purple-500', hover: 'purple-600', light: 'purple-100', text: 'purple-700' },
    orange: { name: 'Naranja', primary: 'orange-500', hover: 'orange-600', light: 'orange-100', text: 'orange-700' },
    cyan: { name: 'Cian', primary: 'cyan-500', hover: 'cyan-600', light: 'cyan-100', text: 'cyan-700' },
    pink: { name: 'Rosa', primary: 'pink-500', hover: 'pink-600', light: 'pink-100', text: 'pink-700' },
    yellow: { name: 'Amarillo', primary: 'yellow-500', hover: 'yellow-600', light: 'yellow-100', text: 'yellow-700' },
    teal: { name: 'Turquesa', primary: 'teal-500', hover: 'teal-600', light: 'teal-100', text: 'teal-700' },
    lime: { name: 'Lima', primary: 'lime-500', hover: 'lime-600', light: 'lime-100', text: 'lime-700' }
  };

  // Cargar configuración de pantalla si hay PIN o ID
  useEffect(() => {
    const loadScreenConfig = async () => {
      try {
        const screenId = searchParams.get('screen');
        let config = null;

        if (pin) {
          // Cargar por PIN code
          const response = await api.get(`/api/screens/by-pin/${pin}`);
          config = response.data;
        } else if (screenId) {
          // Cargar por ID
          const response = await api.get(`/api/screens/${screenId}`);
          config = response.data;
        }

        if (config) {
          setScreenConfig(config);
          // Aplicar configuración
          if (config.settings) {
            setSettings({
              ...settings,
              ...config.settings,
              showResolved: config.filters?.showResolved || false
            });
          }
        }
      } catch (error) {
        console.error('Error cargando configuración de pantalla:', error);
      }
    };

    loadScreenConfig();
  }, [pin, searchParams]);

  // Cargar tickets
  const loadTickets = async () => {
    try {
      const params = {
        limit: 50
      };
      if (!settings.showResolved) {
        // Solo mostrar tickets no resueltos/cerrados
      }
      const response = await api.get('/api/tickets', { params });
      setTickets(response.data.tickets || []);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error cargando tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  // Cargar al montar
  useEffect(() => {
    loadTickets();
  }, [settings.showResolved]);

  // Auto-refresh
  useEffect(() => {
    if (!settings.autoRefresh) return;

    const interval = setInterval(() => {
      loadTickets();
    }, settings.refreshInterval * 1000);

    return () => clearInterval(interval);
  }, [settings.autoRefresh, settings.refreshInterval]);

  // Socket para actualizaciones en tiempo real
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const handleConnect = () => {
      console.log('✅ Pantalla conectada a WebSocket');
      setConnected(true);
    };

    const handleDisconnect = () => {
      console.log('❌ Pantalla desconectada de WebSocket');
      setConnected(false);
    };

    const handleTicketUpdate = (data) => {
      console.log('🔔 Ticket actualizado en tiempo real:', data);
      // Mostrar indicador visual
      setJustUpdated(true);
      setTimeout(() => setJustUpdated(false), 2000);
      // Recargar tickets inmediatamente
      loadTickets();
    };

    // Conectar a todos los eventos de tickets
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('ticket:created', handleTicketUpdate);
    socket.on('ticket:updated', handleTicketUpdate);
    socket.on('ticket:deleted', handleTicketUpdate);
    socket.on('ticket:assigned', handleTicketUpdate);
    socket.on('ticket:status-changed', handleTicketUpdate);
    socket.on('ticket:priority-changed', handleTicketUpdate);

    setConnected(socket.connected);

    // Emitir evento para registrar la pantalla
    if (socket.connected) {
      socket.emit('display:register', {
        screenId: screenConfig?.id || 'unknown',
        pin: pin || null,
        timestamp: new Date().toISOString()
      });
    }

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('ticket:created', handleTicketUpdate);
      socket.off('ticket:updated', handleTicketUpdate);
      socket.off('ticket:deleted', handleTicketUpdate);
      socket.off('ticket:assigned', handleTicketUpdate);
      socket.off('ticket:status-changed', handleTicketUpdate);
      socket.off('ticket:priority-changed', handleTicketUpdate);
    };
  }, [screenConfig, pin]);

  // Formatear fecha
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays}d`;
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
  };

  // Obtener color por prioridad
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critica': return 'bg-red-600 text-white';
      case 'alta': return 'bg-orange-500 text-white';
      case 'media': return 'bg-yellow-500 text-black';
      case 'baja': return 'bg-green-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  // Obtener color de estado
  const getStatusColor = (status) => {
    switch (status) {
      case 'abierto': return 'bg-blue-500';
      case 'asignado': return 'bg-purple-500';
      case 'en_progreso': return 'bg-yellow-500';
      case 'resuelto': return 'bg-green-500';
      case 'cerrado': return 'bg-gray-500';
      default: return 'bg-gray-400';
    }
  };

  // Obtener emoji de gravedad
  const getSeverityIcon = (priority) => {
    switch (priority) {
      case 'critica': return '🔴';
      case 'alta': return '🟠';
      case 'media': return '🟡';
      case 'baja': return '🟢';
      default: return '⚪';
    }
  };

  // Filtrar tickets a mostrar
  const displayTickets = tickets.filter(t => {
    if (settings.showResolved) return true;
    return !['resuelto', 'cerrado'].includes(t.status);
  });

  // Tamaño de fuente
  const fontSizeClass = {
    small: 'text-sm',
    normal: 'text-base',
    large: 'text-lg',
    xlarge: 'text-xl'
  }[settings.fontSize] || 'text-base';

  // Tema
  const themeClasses = settings.theme === 'dark'
    ? 'bg-gray-900 text-white'
    : 'bg-gray-100 text-gray-900';

  const cardTheme = settings.theme === 'dark'
    ? 'bg-gray-800 border-gray-700'
    : 'bg-white border-gray-200';

  return (
    <div className={`min-h-screen ${themeClasses} p-4`}>
      {/* Header */}
      <header className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <img
            src="/logo-xia.svg"
            alt="XIA"
            className="h-10 mr-4"
            style={settings.theme === 'dark' ? { filter: 'brightness(0) invert(1)' } : {}}
          />
          <div>
            <h1 className={`font-bold ${fontSizeClass === 'text-sm' ? 'text-lg' : 'text-2xl'}`}>
              Panel de Incidencias
            </h1>
            <p className="text-sm opacity-70">
              Actualizacion en tiempo real
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          {/* Indicador de actualización en vivo */}
          {justUpdated && (
            <div className="flex items-center px-3 py-1 rounded-full text-sm bg-blue-500/20 text-blue-400 animate-pulse">
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Actualizando...
            </div>
          )}

          {/* Estado de conexion */}
          <div className={`flex items-center px-3 py-1 rounded-full text-sm transition-all ${
            connected ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
          } ${justUpdated ? 'ring-2 ring-green-400' : ''}`}>
            {connected ? <Wifi className="w-4 h-4 mr-2" /> : <WifiOff className="w-4 h-4 mr-2" />}
            {connected ? 'Tiempo Real' : 'Desconectado'}
          </div>

          {/* Ultima actualizacion */}
          <div className="flex items-center text-sm opacity-70">
            <Clock className="w-4 h-4 mr-2" />
            {lastUpdate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </div>

          {/* Refresh manual */}
          <button
            onClick={loadTickets}
            className={`p-2 rounded-lg transition ${
              settings.theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
            }`}
            title="Actualizar"
          >
            <RefreshCw className="w-5 h-5" />
          </button>

          {/* Settings */}
          <button
            onClick={() => setShowSettings(!showSettings)}
            className={`p-2 rounded-lg transition ${
              settings.theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
            }`}
            title="Configuracion"
          >
            <Settings className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Settings Panel */}
      {showSettings && (
        <div className={`mb-6 p-4 rounded-xl ${cardTheme} border`}>
          <h3 className="font-medium mb-4">Configuracion de Pantalla</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm mb-1 opacity-70">Actualizar cada</label>
              <select
                value={settings.refreshInterval}
                onChange={(e) => setSettings({ ...settings, refreshInterval: parseInt(e.target.value) })}
                className="w-full px-3 py-2 rounded-lg bg-gray-700 border border-gray-600"
              >
                <option value={10}>10 segundos</option>
                <option value={30}>30 segundos</option>
                <option value={60}>1 minuto</option>
                <option value={300}>5 minutos</option>
              </select>
            </div>
            <div>
              <label className="block text-sm mb-1 opacity-70">Tamano de texto</label>
              <select
                value={settings.fontSize}
                onChange={(e) => setSettings({ ...settings, fontSize: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-gray-700 border border-gray-600"
              >
                <option value="small">Pequeno</option>
                <option value="normal">Normal</option>
                <option value="large">Grande</option>
                <option value="xlarge">Muy grande</option>
              </select>
            </div>
            <div>
              <label className="block text-sm mb-1 opacity-70">Tema</label>
              <select
                value={settings.theme}
                onChange={(e) => setSettings({ ...settings, theme: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-gray-700 border border-gray-600"
              >
                <option value="dark">Oscuro</option>
                <option value="light">Claro</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.showResolved}
                  onChange={(e) => setSettings({ ...settings, showResolved: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm">Mostrar resueltos</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Stats Bar */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className={`p-4 rounded-xl ${cardTheme} border`}>
          <div className="text-3xl font-bold text-blue-500">
            {displayTickets.filter(t => t.status === 'abierto').length}
          </div>
          <div className="text-sm opacity-70">Abiertos</div>
        </div>
        <div className={`p-4 rounded-xl ${cardTheme} border`}>
          <div className="text-3xl font-bold text-purple-500">
            {displayTickets.filter(t => t.status === 'en_progreso').length}
          </div>
          <div className="text-sm opacity-70">En Progreso</div>
        </div>
        <div className={`p-4 rounded-xl ${cardTheme} border`}>
          <div className="text-3xl font-bold text-red-500">
            {displayTickets.filter(t => t.priority === 'critica').length}
          </div>
          <div className="text-sm opacity-70">Criticas</div>
        </div>
        <div className={`p-4 rounded-xl ${cardTheme} border`}>
          <div className="text-3xl font-bold text-green-500">
            {tickets.filter(t => t.status === 'resuelto').length}
          </div>
          <div className="text-sm opacity-70">Resueltas Hoy</div>
        </div>
      </div>

      {/* Tickets Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin opacity-50" />
        </div>
      ) : displayTickets.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 opacity-50">
          <AlertTriangle className="w-16 h-16 mb-4" />
          <p className="text-xl">No hay incidencias pendientes</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {displayTickets.map((ticket, index) => (
            <div
              key={ticket.id}
              className={`rounded-xl ${cardTheme} border overflow-hidden transition-all hover:scale-[1.02] animate-fade-in`}
              style={{ animationDelay: `${index * 50}ms` }}
            >
              {/* Barra de estado superior */}
              <div className={`h-2 ${getStatusColor(ticket.status)}`} />

              <div className="p-4">
                {/* Header con prioridad */}
                <div className="flex items-start justify-between mb-3">
                  <span className={`px-2 py-1 rounded text-xs font-bold ${getPriorityColor(ticket.priority)}`}>
                    {getSeverityIcon(ticket.priority)} {ticket.priority?.toUpperCase()}
                  </span>
                  <span className="text-xs opacity-50">
                    #{ticket.ticket_number || ticket.id?.slice(0, 8)}
                  </span>
                </div>

                {/* Titulo */}
                <h3 className={`font-semibold mb-2 line-clamp-2 ${fontSizeClass}`}>
                  {ticket.title}
                </h3>

                {/* Info */}
                <div className="space-y-2 text-sm">
                  {/* Fecha apertura */}
                  <div className="flex items-center opacity-70">
                    <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span>Abierto: {formatDate(ticket.created_at)}</span>
                  </div>

                  {/* Asignado a */}
                  <div className="flex items-center">
                    <User className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span className={ticket.assigned_to_name ? '' : 'opacity-50 italic'}>
                      {ticket.assigned_to_name || 'Sin asignar'}
                    </span>
                  </div>

                  {/* Estado */}
                  <div className="flex items-center justify-between pt-2 border-t border-gray-700/50">
                    <span className={`px-2 py-1 rounded text-xs ${
                      settings.theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`}>
                      {ticket.status?.replace('_', ' ')}
                    </span>
                    {ticket.category && (
                      <span className="text-xs opacity-50">
                        {ticket.category}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      <footer className="mt-8 text-center text-sm opacity-50">
        <p>Manantial Platform - Panel de Incidencias en Tiempo Real</p>
        <p>Actualiza automaticamente cada {settings.refreshInterval} segundos</p>
      </footer>
    </div>
  );
}
